/**
 * @fileoverview What Today shows first (B30, design revision 2): a gardener
 * that has never had a plant sees only "Your garden starts here" and the
 * two doors (B96), at once, without waiting on the Today brief, the outlook or
 * the weather.
 *
 * What it needs is whether the garden has started (B64): the server's word
 * from the garden place (GET /garden-place, `started`: the garden has had a
 * plant of its own, ever; archiving or deleting every plant does not unstart
 * it). A garden with every plant archived is started, so it is never the
 * doors. The app counts nothing (C118 F1/F3): not its plants list, and no
 * older-server fallback. Until the place is known Today keeps its normal
 * loading state, so a returning gardener never sees the doors (C97). After
 * a plant is saved the place is read again (PlantsContext refreshGarden), so
 * the doors go with the server's fresh word. A garden the server says has no
 * usable postcode keeps the postcode step.
 *
 * The first step (the place step, reads.empty_garden) is the brief's, with
 * the brief's own `started` beside it (C118 F4): Today reads one answer for
 * it, and picks between nothing.
 *
 * Display choice only: nothing about frost, weather or the season is decided
 * here (the brief's, the server's).
 *
 * Where: TodayPage.
 *
 * @module today/lib/todayStart
 */

/** What the decision is told. */
export interface TodayStartInput {
  /** The server has said the garden has no postcode Grow can plan from. */
  noZip: boolean;
  /** The server has said where the garden is (useGardenPostcode.known): until then noZip is not yet true, and the doors must not flash before the postcode step. */
  placeKnown: boolean;
  /** The server's word that the garden has had a plant of its own (GET /garden-place started, B64); only read once placeKnown. */
  started: boolean;
}

/**
 * Whether Today shows only the start doors now.
 *
 * @param input - What the server said about the postcode and whether the garden has started.
 * @returns True when Today shows just the heading and the two doors, with no frost or weather line.
 */
export function showDoorsOnly(input: TodayStartInput): boolean {
  // The postcode step comes first.
  if (!input.placeKnown || input.noZip) return false;
  // The server knows (B64): a garden that has never had a plant is the doors, one that has is not, whatever the app's list holds.
  return !input.started;
}

/**
 * What the brief's first-step read (reads.empty_garden) may do (B30 rev 6, C118 F4). The read is the brief's, and so is
 * `started` (the brief's own word that the garden has had a plant): a started brief never draws the place step. After a
 * plant is saved the held brief is older than the garden; while it is read again (`rereading`) Today waits behind its
 * normal loader, and after `timedOut` draws what it has.
 *
 * @param input - Whether a first-step read is placed, the brief's `started`, whether the brief is being read again after a write, and whether the wait is over.
 * @returns `useTip`: draw the place step; `hold`: keep Today's loader for now.
 */
export function firstStepDecision(input: { tipPlaced: boolean; started: boolean; rereading: boolean; timedOut: boolean }): { useTip: boolean; hold: boolean } {
  if (!input.tipPlaced || input.started) return { useTip: false, hold: false };
  if (input.rereading && !input.timedOut) return { useTip: false, hold: true };
  return { useTip: true, hold: false };
}
